import { useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { filterValuesSeparatorSplitRegex } from 'shared-helpers/consts';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { Button, EmptyIndicator, User } from 'components/common';
import {
  UsersDropdown,
  UsersFilters,
  UsersListHeader,
  UsersPreview,
  UsersViewAchievement,
  UsersViewAttributes,
  UsersViewCompleted,
  UsersViewCourse,
  UsersViewOverview,
  UsersViewPath
} from '.';

import type { EmailTemplateData } from 'shared-types';
import type { UpdateUserObject } from './Users';

interface Props {
  emailTemplates: EmailTemplateData[];
  emailTemplatesDefault: EmailTemplateData;
  updateUsers: (updateUser: UpdateUserObject) => Promise<void>;
}

const UsersList = ({
  emailTemplates,
  emailTemplatesDefault,
  updateUsers
}: Props) => {
  const {
    messages,
    pageState: { loading, users },
    user: currentUser
  } = useReduxState();

  const router = useRouter();

  const [userModalUserId, setUserModalUserId] = useState<number>(null);
  const [userModalVisible, setUserModalVisible] = useState(false);

  const handleUserModalHide = () => setUserModalVisible(false);

  const handleUserPreview = (ev: React.SyntheticEvent<HTMLButtonElement>) => {
    ev.stopPropagation();

    const userId = ev.currentTarget.getAttribute('data-id');
    setUserModalUserId(+userId);
    setUserModalVisible(true);
  };

  const handleUserUpdatedOrDeleted = async () => {
    await updateUsers({ showLoading: false });
  };

  const ElementView = {
    achievement: UsersViewAchievement,
    attributes: UsersViewAttributes,
    completed: UsersViewCompleted,
    course: UsersViewCourse,
    overview: UsersViewOverview,
    path: UsersViewPath
  }[
    (router?.query?.view as string)?.split(
      filterValuesSeparatorSplitRegex
    )?.[0] || 'overview'
  ];

  const currentUserIsSuperadmin = currentUser?.roles?.includes('superadmin');
  const currentUserIsPermitted =
    currentUserIsSuperadmin ||
    currentUser?.permissions.includes('manage-users');

  return (
    <div className="users">
      <Box
        headerContent={
          <UsersListHeader
            emailTemplates={emailTemplates}
            emailTemplatesDefault={emailTemplatesDefault}
            updateUsers={updateUsers}
          />
        }
        headerContentWrap={true}
        loading={loading}
        padding={0}
        subHeaderContent={<UsersFilters />}
      >
        {users && users.length > 0 ? (
          <BoxOverflowWrapper withStickyHeader={true}>
            <BoxTable>
              <BoxTableHeader
                style={{ whiteSpace: 'nowrap' }}
                withCheckbox={true}
                withCheckboxIdentifier="users-page"
              >
                <BoxTableCell sticky={true}>{messages.commonUser}</BoxTableCell>

                <ElementView loggedInUser={currentUser} returnPart="header" />

                <BoxTableCell></BoxTableCell>
                {currentUserIsPermitted ? (
                  <BoxTableCell sticky={true} stickyAnchor="right" />
                ) : null}
              </BoxTableHeader>

              {users.map((user) => (
                <BoxTableRow
                  key={user.id}
                  hideCheckBox={user?.isLimitedUser}
                  withCheckboxValue={user?.isLimitedUser ? null : user.id}
                >
                  <BoxTableCell sticky={true} stickyBackgroundColor="white">
                    <User
                      enableProfileLink={true}
                      // the content may overflow for some reason inside a table cell
                      // causing no spacing between this and the next cell, this
                      // hack should fix it
                      style={{ paddingRight: 20, minWidth: 200 }}
                      user={user}
                    />
                  </BoxTableCell>

                  <ElementView
                    loggedInUser={currentUser}
                    returnPart="body"
                    user={user}
                  />

                  <BoxTableCell shrink={true}>
                    <Button
                      category="lighter"
                      data-id={user.id}
                      onClick={handleUserPreview}
                      icon="search"
                      size="small"
                      style={{ borderRadius: 30 }}
                      text={messages.usersPreview}
                    />
                  </BoxTableCell>

                  {(currentUserIsPermitted && !user.isLimitedUser) ||
                  (user.isLimitedUser && currentUserIsSuperadmin) ? (
                    <BoxTableCell
                      sticky={true}
                      stickyAnchor="right"
                      stickyBackgroundColor="white"
                      shrink={true}
                    >
                      {!user.roles?.includes('vuoadmin') ? (
                        <UsersDropdown
                          emailTemplates={emailTemplates}
                          emailTemplatesDefault={emailTemplatesDefault}
                          onUserDeleted={handleUserUpdatedOrDeleted}
                          onUserUpdated={handleUserUpdatedOrDeleted}
                          targetUser={user}
                        />
                      ) : null}
                    </BoxTableCell>
                  ) : null}
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        ) : users?.length === 0 ? (
          <EmptyIndicator icon="user" title={messages.usersNoUsersFound} />
        ) : null}
      </Box>

      <UsersPreview
        onHide={handleUserModalHide}
        isVisible={userModalVisible}
        targetUserId={userModalUserId}
      />

      <style jsx>{`
        .badge {
          display: inline-block;
          margin: 2px 4px 2px 0;
        }
      `}</style>
    </div>
  );
};

export default UsersList;
